import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import AsyncExportReadyDialog from '~/features/exports/components/asyncExportReadyDialog'
import { EXPORT_POLL_INTERVAL_MS } from '~/features/exports/constants'
import useDownloadExportJob from '~/features/exports/hooks/useDownloadExportJob'
import { TAsyncExportJob, EExportJobStatus } from '~/features/exports/models'
import { pollExportJob } from '~/features/exports/services/asyncExportApi'
import asyncExportJobsStore from '~/features/exports/store/asyncExportJobsStore'
import isOnExportOriginPage from '~/features/exports/utils/isOnExportOriginPage'

const IN_PROGRESS_STATUSES: EExportJobStatus[] = [
  EExportJobStatus.pending,
  EExportJobStatus.processing,
]

function shouldShowReadyModal(job: TAsyncExportJob): boolean {
  return (
    job.status === EExportJobStatus.complete &&
    !job.readyModalDismissed &&
    !job.readyModalShown
  )
}

function AsyncExportJobsCoordinator() {
  const { pathname } = useLocation()
  const [modalJobId, setModalJobId] = useState<string | null>(null)
  const pollingRef = useRef(false)
  const downloadMutation = useDownloadExportJob()

  const modalJob = asyncExportJobsStore(
    (state) => state.jobs.find((job) => job.jobId === modalJobId) ?? null,
  )

  const openReadyModalIfNeeded = useCallback(
    (job: TAsyncExportJob) => {
      if (!shouldShowReadyModal(job)) {
        return
      }

      if (isOnExportOriginPage(job.type, pathname)) {
        asyncExportJobsStore.getState().markModalShown(job.jobId)
        return
      }

      asyncExportJobsStore.getState().markModalShown(job.jobId)
      setModalJobId(job.jobId)
    },
    [pathname],
  )

  const pollJobs = useCallback(async () => {
    if (pollingRef.current) {
      return
    }
    pollingRef.current = true

    try {
      const { jobs, updateJob, removeJob } = asyncExportJobsStore.getState()
      const inProgress = jobs.filter((job) =>
        IN_PROGRESS_STATUSES.includes(job.status),
      )

      await Promise.all(
        inProgress.map(async (job) => {
          try {
            const result = await pollExportJob(job.jobId, job.type)
            if (!result) {
              removeJob(job.jobId)
              toast.error('Export expired, please start a new one.')
              return
            }

            const previousStatus = job.status
            updateJob(job.jobId, {
              status: result.status,
              error: result.error,
              downloadFilename: result.downloadFilename,
              meta: result.meta,
            })

            if (
              result.status === EExportJobStatus.failed &&
              previousStatus !== EExportJobStatus.failed
            ) {
              toast.error(result.error || 'Export failed')
            }

            if (
              result.status === EExportJobStatus.complete &&
              previousStatus !== EExportJobStatus.complete
            ) {
              const updatedJob = asyncExportJobsStore
                .getState()
                .jobs.find((item) => item.jobId === job.jobId)
              if (updatedJob) {
                openReadyModalIfNeeded(updatedJob)
              }
            }
          } catch {
            // Keep polling on transient network errors.
          }
        }),
      )
    } finally {
      pollingRef.current = false
    }
  }, [openReadyModalIfNeeded])

  useEffect(() => {
    asyncExportJobsStore.getState().pruneStaleJobs()

    const { jobs } = asyncExportJobsStore.getState()
    const pendingModalJob = jobs.find(
      (job) =>
        shouldShowReadyModal(job) && !isOnExportOriginPage(job.type, pathname),
    )
    if (pendingModalJob) {
      openReadyModalIfNeeded(pendingModalJob)
    }

    pollJobs()
    const intervalId = window.setInterval(pollJobs, EXPORT_POLL_INTERVAL_MS)
    return () => window.clearInterval(intervalId)
  }, [openReadyModalIfNeeded, pathname, pollJobs])

  const handleModalClose = () => {
    setModalJobId(null)
  }

  const handleModalDismiss = () => {
    if (modalJobId) {
      asyncExportJobsStore.getState().markModalDismissed(modalJobId)
    }
    handleModalClose()
  }

  const handleModalDownload = () => {
    if (!modalJob) {
      return
    }
    downloadMutation.mutate(
      {
        jobId: modalJob.jobId,
        type: modalJob.type,
        downloadFilename: modalJob.downloadFilename,
      },
      {
        onSuccess: () => {
          handleModalClose()
        },
      },
    )
  }

  return (
    <AsyncExportReadyDialog
      job={modalJob}
      isOpen={modalJobId != null}
      isDownloading={downloadMutation.isPending}
      onClose={handleModalClose}
      onDismiss={handleModalDismiss}
      onDownload={handleModalDownload}
    />
  )
}

export default AsyncExportJobsCoordinator
